/**
 * MorrFit Theme - Main JavaScript
 *
 * @package MorrFitHP
 */

// Mobile Menu Toggle
class MobileMenu {
  constructor() {
    this.toggle = document.getElementById('mobile-menu-toggle');
    this.menu = document.getElementById('mobile-menu');
    
    if (this.toggle && this.menu) {
      this.init();
    }
  }

  init() {
    this.toggle.addEventListener('click', () => this.toggleMenu());
    
    // Close menu on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.closeMenu();
      }
    });

    // Close menu when clicking outside
    document.addEventListener('click', (e) => {
      if (this.isOpen() && !this.toggle.contains(e.target) && !this.menu.contains(e.target)) {
        this.closeMenu();
      }
    });
  }

  toggleMenu() {
    if (this.isOpen()) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }

  openMenu() {
    this.menu.classList.remove('hidden');
    this.menu.classList.add('is-open');
    this.toggle.classList.add('is-active');
    this.toggle.setAttribute('aria-expanded', 'true');
  }

  closeMenu() {
    this.menu.classList.add('hidden');
    this.menu.classList.remove('is-open');
    this.toggle.classList.remove('is-active');
    this.toggle.setAttribute('aria-expanded', 'false');
  }

  isOpen() {
    return this.menu.classList.contains('is-open');
  }
}

// Smooth Scroll for Anchor Links
class SmoothScroll {
  constructor() {
    this.init();
  }

  init() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', (e) => this.handleClick(e, anchor));
    });
  }

  handleClick(e, anchor) {
    const href = anchor.getAttribute('href');
    
    // Skip if it's just "#" or empty
    if (href === '#' || href === '') {
      return;
    }

    const target = document.querySelector(href);
    
    if (target) {
      e.preventDefault();
      
      const headerOffset = 80; // Account for sticky header
      const elementPosition = target.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }
}

// Sticky Header with Hide on Scroll
class StickyHeader {
  constructor() {
    this.header = document.getElementById('masthead');
    this.lastScrollY = 0;
    this.ticking = false;
    
    if (this.header) {
      this.init();
    }
  }

  init() {
    window.addEventListener('scroll', () => this.onScroll());
  }

  onScroll() {
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        this.update();
        this.ticking = false;
      });
      this.ticking = true;
    }
  }

  update() {
    const currentScrollY = window.scrollY;
    
    // Add shadow when scrolled
    if (currentScrollY > 10) {
      this.header.classList.add('shadow-md');
    } else {
      this.header.classList.remove('shadow-md');
    }

    this.lastScrollY = currentScrollY;
  }
}

// Lazy Loading Images (Progressive Enhancement)
class LazyImages {
  constructor() {
    this.images = document.querySelectorAll('img[data-src]');
    
    if ('IntersectionObserver' in window && this.images.length > 0) {
      this.init();
    } else {
      this.loadAll();
    }
  }

  init() {
    const options = {
      root: null,
      rootMargin: '50px',
      threshold: 0.1
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadImage(entry.target);
          this.observer.unobserve(entry.target);
        }
      });
    }, options);

    this.images.forEach(img => this.observer.observe(img));
  }

  loadImage(img) {
    const src = img.getAttribute('data-src');
    if (src) {
      img.src = src;
      img.removeAttribute('data-src');
    }
  }

  loadAll() {
    this.images.forEach(img => this.loadImage(img));
  }
}

// Animation on Scroll (Simple reveal)
class ScrollReveal {
  constructor() {
    this.elements = document.querySelectorAll('[data-reveal]');
    
    if ('IntersectionObserver' in window && this.elements.length > 0) {
      this.init();
    }
  }

  init() {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
          this.observer.unobserve(entry.target);
        }
      });
    }, options);

    this.elements.forEach(el => {
      el.classList.add('reveal-hidden');
      this.observer.observe(el);
    });
  }
}

// Initialize on DOM Ready
document.addEventListener('DOMContentLoaded', () => {
  new MobileMenu();
  new SmoothScroll();
  new StickyHeader();
  new LazyImages();
  new ScrollReveal();
});

// Export for external use
if (typeof window !== 'undefined') {
  window.MorrFitHP = {
    MobileMenu,
    SmoothScroll,
    StickyHeader,
    LazyImages,
    ScrollReveal
  };
}
